import { removeTrailingZeros } from '@cryptoboyio/mercuryo-lab/utils';
import { createReducer } from '@reduxjs/toolkit';

import { loadersBuilder, logoutBuilder } from '@modules/builders';

import transactionsActions from './actions';
import initialState from './state';
import type * as TransactionsTypes from './types';

export const getSortedTransactions = (
  transactions: TransactionsTypes.TransactionType[],
  oldTransactions = {}
): TransactionsTypes.TSortTransactions => {
  const result = transactions.reduce((acc, transaction) => {
    const [date] = transaction.created_at.split(' ');
    const timestamp = Number(new Date(date));
    if (!acc[timestamp]) acc[timestamp] = {};
    acc[timestamp][transaction.id] = transaction;
    const { created_at_ts, updated_at_ts, amount, fee, calculated_amount } =
      acc[timestamp][transaction.id];
    acc[timestamp][transaction.id].created_at_ts = created_at_ts * 1000;
    acc[timestamp][transaction.id].updated_at_ts = updated_at_ts * 1000;
    acc[timestamp][transaction.id].amount = removeTrailingZeros(amount);
    if (fee) acc[timestamp][transaction.id].fee = removeTrailingZeros(fee);
    if (calculated_amount)
      acc[timestamp][transaction.id].calculated_amount = removeTrailingZeros(calculated_amount);
    return acc;
  }, oldTransactions as TransactionsTypes.TSortTransactions);
  return Object.entries(result).reduce((acc, [key, value]) => {
    const res = Object.values(value)
      .sort((key1, key2) => key2.created_at_ts - key1.created_at_ts)
      .reduce((sortedTransactions, item) => {
        sortedTransactions[item.id] = item;
        return sortedTransactions;
      }, {});
    acc[key] = res;
    return acc;
  }, {});
};

export const transactionsReducer = createReducer(initialState, builder => {
  builder.addCase(transactionsActions.getTransactions.fulfilled, (state, action) => {
    const offset = action.meta.arg?.offset;
    const prevList = offset ? state.list : {};
    const nextList = getSortedTransactions(action.payload.data, prevList);
    state.list = { ...prevList, ...nextList };
    state.total = action.payload.total;
  });

  builder.addCase(transactionsActions.getReferral.fulfilled, (state, action) => {
    state.referral.data = action.payload.data;
  });

  builder.addCase(transactionsActions.getReferralTransactions.fulfilled, (state, action) => {
    state.referral.list = state.referral.list.concat(action.payload.data);
  });

  builder.addCase(transactionsActions.resetTransactions, state => {
    state.list = [];
    state.total = 0;
  });

  builder.addCase(transactionsActions.resetReferralList, state => {
    state.referral.list = [];
  });

  logoutBuilder(initialState, builder);
  loadersBuilder('transactions', builder);
});

export { transactionsActions, TransactionsTypes };
